import React, { useState, useEffect, useCallback } from 'react';

interface TDEEProfile {
  heightCm: number | null;
  weightLbs: number | null;
  weightKg: number | null;
  age: number | null;
  gender: string | null;
  activityLevel: string | null;
  targetWeightLossPerWeek: number | null;
  goalWeight: number | null;
  startingWeight: number | null;
}

interface CICCalculatorProps {
  clientId: string;
  /** Average logged calories for the selected period */
  avgCalories: number | null;
  /** Weight at start of selected period */
  periodStartWeight: number | null;
  /** Weight at end of selected period */
  periodEndWeight: number | null;
  /** Number of days in the selected period */
  periodDays: number;
  /** Selection label (e.g., "Mar 3–9" or "March 2026") */
  periodLabel: string;
}

// Activity level multipliers (Mifflin-St Jeor standard)
const ACTIVITY_MULTIPLIERS: Record<string, { label: string; factor: number }> = {
  sedentary:         { label: 'Sedentary',          factor: 1.2 },
  lightly_active:    { label: 'Lightly Active',     factor: 1.375 },
  moderately_active: { label: 'Moderately Active',  factor: 1.55 },
  very_active:       { label: 'Very Active',        factor: 1.725 },
  extra_active:      { label: 'Extra Active',       factor: 1.9 },
};

const CAL_PER_LB = 3500;

/**
 * CIC Calculator Tile — Calories In vs Calories Out
 *
 * Computes TDEE from profile data + activity level,
 * then compares expected vs actual weight change to identify
 * "phantom calorie" drift.
 */
export const CICCalculatorTile: React.FC<CICCalculatorProps> = ({
  clientId,
  avgCalories,
  periodStartWeight,
  periodEndWeight,
  periodDays,
  periodLabel
}) => {
  const [profile, setProfile] = useState<TDEEProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [localActivityLevel, setLocalActivityLevel] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showDailyVariance, setShowDailyVariance] = useState(false);

  // Fetch TDEE profile data
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token || !clientId) return;

    setLoading(true);
    const abortController = new AbortController();

    fetch(`/api/coach/clients/${clientId}/tdee-profile`, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      signal: abortController.signal
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setProfile(data);
          setLocalActivityLevel(data.activityLevel);
        }
        setLoading(false);
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.error('Failed to fetch TDEE profile:', err);
          setLoading(false);
        }
      });

    return () => abortController.abort();
  }, [clientId]);

  // Save activity level when changed
  const handleActivityChange = useCallback(async (level: string) => {
    setLocalActivityLevel(level);

    const token = localStorage.getItem('auth_token');
    if (!token) return;

    setSaving(true);
    try {
      await fetch(`/api/coach/clients/${clientId}/tdee-profile`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ activity_level: level })
      });
      // Update local profile state
      setProfile(prev => prev ? { ...prev, activityLevel: level } : prev);
    } catch (err) {
      console.error('Failed to save activity level:', err);
    } finally {
      setSaving(false);
    }
  }, [clientId]);

  // --- TDEE Calculation (Mifflin-St Jeor) ---
  const activityLevel = localActivityLevel || 'sedentary';
  const activityInfo = ACTIVITY_MULTIPLIERS[activityLevel] || ACTIVITY_MULTIPLIERS.sedentary;

  let bmr: number | null = null;
  let tdee: number | null = null;

  if (profile?.weightKg && profile?.heightCm && profile?.age && profile?.gender) {
    // Mifflin-St Jeor
    bmr = (10 * profile.weightKg) + (6.25 * profile.heightCm) - (5 * profile.age);
    bmr += profile.gender === 'male' ? 5 : -161;
    bmr = Math.round(bmr);
    tdee = Math.round(bmr * activityInfo.factor);
  }

  // --- CIC Calculation ---
  // targetWeightLossPerWeek: positive = losing weight, negative = gaining weight
  const targetLossPerWeek = profile?.targetWeightLossPerWeek ?? null;
  const dailyDeficit = targetLossPerWeek != null ? Math.round((targetLossPerWeek * CAL_PER_LB) / 7) : null;
  const targetDailyCal = tdee && dailyDeficit != null ? tdee - dailyDeficit : null;

  // Weight change over the period
  const actualWeightChange = (periodStartWeight != null && periodEndWeight != null)
    ? Math.round((periodEndWeight - periodStartWeight) * 10) / 10
    : null;

  // --- Roll-down calculation ---
  // Target loss for the period (scaled from weekly target)
  const targetLossForPeriod = targetLossPerWeek
    ? Math.round((targetLossPerWeek * periodDays / 7) * 10) / 10
    : null;

  // Part 1: Logging Gap — difference between target cal and what they logged
  // Positive = ate more than plan, negative = ate less than plan
  let loggingGapCalPerDay: number | null = null;
  let loggingGapLbs: number | null = null;
  if (targetDailyCal && avgCalories != null) {
    loggingGapCalPerDay = Math.round(avgCalories - targetDailyCal);
    loggingGapLbs = Math.round((loggingGapCalPerDay * periodDays / CAL_PER_LB) * 10) / 10;
  }

  // Expected loss based on logged calories (not target, but what they actually ate)
  let expectedLossFromLogged: number | null = null;
  if (tdee && avgCalories != null && periodDays > 0) {
    const loggedDeficitPerDay = tdee - avgCalories;
    expectedLossFromLogged = Math.round((loggedDeficitPerDay * periodDays / CAL_PER_LB) * 10) / 10;
  }

  // Part 2: Phantom Gap — difference between expected loss (from logged) and actual loss
  // Positive = lost less than expected (unexplained), negative = lost more than expected
  let phantomGapLbs: number | null = null;
  let phantomCalPerDay: number | null = null;
  if (expectedLossFromLogged !== null && actualWeightChange !== null) {
    // expectedLossFromLogged is positive when deficit exists (e.g., 0.6 means should lose 0.6)
    // actualWeightChange is negative when losing (e.g., -0.2)
    phantomGapLbs = Math.round((expectedLossFromLogged + actualWeightChange) * 10) / 10;
    phantomCalPerDay = Math.round((phantomGapLbs * CAL_PER_LB) / periodDays);
  }

  // Missing data checks
  const missingProfile = !profile?.weightKg || !profile?.heightCm || !profile?.age || !profile?.gender;

  if (loading) {
    return (
      <div className="cic-calculator">
        <h3 className="cal-panel-title">CIC Calculator</h3>
        <div className="cic-loading"><div className="loading-spinner" /></div>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="cic-calculator">
      <h3 className="cal-panel-title">CIC Calculator</h3>

      <div className="cic-content">
        {missingProfile ? (
          <div className="cic-missing">
            <span className="cic-missing-text">Missing profile data</span>
            <span className="cic-missing-detail">
              Need: {!profile?.heightCm ? 'height, ' : ''}{!profile?.weightKg ? 'weight, ' : ''}{!profile?.age ? 'DOB, ' : ''}{!profile?.gender ? 'gender' : ''}
            </span>
          </div>
        ) : (
          <>
            {/* TDEE Section */}
            <div className="cic-section">
              <div className="cic-row">
                <span className="cic-label">BMR / TDEE</span>
                <span className="cic-value">
                  {bmr?.toLocaleString()} <span className="cic-tdee-value">/ {tdee?.toLocaleString()}</span>
                </span>
              </div>
              {targetDailyCal && (
                <div className="cic-row">
                  <span className="cic-label">Target Cal</span>
                  <span className="cic-value">{targetDailyCal.toLocaleString()}</span>
                </div>
              )}
            </div>

            {/* CIC Roll-Down */}
            {avgCalories != null && tdee && targetLossForPeriod !== null ? (
              <>
                <div className="cic-section">
                  <div className="cic-row">
                    <span className="cic-label">Target / Expected</span>
                    <span className="cic-value">
                      {targetLossForPeriod > 0 ? '-' : targetLossForPeriod < 0 ? '+' : ''}{targetLossForPeriod > 0 ? targetLossForPeriod : targetLossForPeriod < 0 ? Math.abs(targetLossForPeriod) : 0}
                      {' / '}
                      {expectedLossFromLogged !== null
                        ? <>{expectedLossFromLogged > 0 ? '-' : expectedLossFromLogged < 0 ? '+' : ''}{Math.round(Math.abs(expectedLossFromLogged) * 10) / 10}</>
                        : '—'
                      }
                      {' lbs'}
                    </span>
                  </div>
                  {actualWeightChange !== null && (
                    <div className="cic-row cic-row-highlight">
                      <span className="cic-label">Actual</span>
                      <span className={`cic-value cic-actual-value ${actualWeightChange < 0 ? 'cic-green' : actualWeightChange > 0 ? 'cic-red' : ''}`}>
                        {actualWeightChange > 0 ? '+' : ''}{actualWeightChange} lbs
                      </span>
                    </div>
                  )}
                </div>

                <div className="cic-section">
                  <div
                    className={`cic-row cic-row-gap cic-row-clickable ${loggingGapCalPerDay != null && loggingGapCalPerDay > 0 ? 'cic-row-gap-red' : loggingGapCalPerDay != null && loggingGapCalPerDay < 0 ? 'cic-row-gap-green' : 'cic-row-gap-neutral'}`}
                    onClick={() => setShowDailyVariance(v => !v)}
                  >
                    <span className="cic-label">Logging Δ</span>
                    <span className={`cic-value ${loggingGapLbs != null && loggingGapLbs > 0 ? 'cic-red' : loggingGapLbs != null && loggingGapLbs < 0 ? 'cic-green' : ''}`}>
                      {loggingGapCalPerDay != null && loggingGapLbs != null
                        ? showDailyVariance
                          ? <>{loggingGapCalPerDay > 0 ? '+' : ''}{loggingGapCalPerDay.toLocaleString()} cal/day :: {loggingGapLbs > 0 ? '+' : ''}{loggingGapLbs} lbs</>
                          : <>{loggingGapCalPerDay > 0 ? '+' : ''}{(loggingGapCalPerDay * periodDays).toLocaleString()} cal :: {loggingGapLbs > 0 ? '+' : ''}{loggingGapLbs} lbs</>
                        : '0 cal :: 0 lbs'
                      }
                    </span>
                  </div>
                  <div
                    className={`cic-row cic-row-gap cic-row-clickable ${phantomCalPerDay != null && phantomCalPerDay > 0 ? 'cic-row-gap-red' : phantomCalPerDay != null && phantomCalPerDay < 0 ? 'cic-row-gap-green' : 'cic-row-gap-neutral'}`}
                    onClick={() => setShowDailyVariance(v => !v)}
                  >
                    <span className="cic-label">Phantom Δ</span>
                    <span className={`cic-value ${phantomGapLbs != null && phantomGapLbs > 0 ? 'cic-red' : phantomGapLbs != null && phantomGapLbs < 0 ? 'cic-green' : ''}`}>
                      {phantomGapLbs != null && phantomCalPerDay != null
                        ? showDailyVariance
                          ? <>{phantomCalPerDay > 0 ? '+' : ''}{phantomCalPerDay.toLocaleString()} cal/day :: {phantomGapLbs > 0 ? '+' : ''}{phantomGapLbs} lbs</>
                          : <>{phantomCalPerDay > 0 ? '+' : ''}{(phantomCalPerDay * periodDays).toLocaleString()} cal :: {phantomGapLbs > 0 ? '+' : ''}{phantomGapLbs} lbs</>
                        : '0 cal :: 0 lbs'
                      }
                    </span>
                  </div>
                </div>
              </>
            ) : avgCalories != null && tdee ? (
              /* No target set — show basic comparison */
              <div className="cic-section">
                <div className="cic-period-label">{periodLabel}</div>
                <div className="cic-row">
                  <span className="cic-label">Avg Logged</span>
                  <span className="cic-value">{Math.round(avgCalories).toLocaleString()}</span>
                </div>
                <div className="cic-row">
                  <span className="cic-label">vs TDEE</span>
                  <span className={`cic-value ${avgCalories < tdee ? 'cic-green' : 'cic-red'}`}>
                    {avgCalories < tdee ? '' : '+'}{Math.round(avgCalories - tdee).toLocaleString()} cal/day
                  </span>
                </div>
                {actualWeightChange !== null && (
                  <div className="cic-row">
                    <span className="cic-label">Actual Δ wt</span>
                    <span className={`cic-value ${actualWeightChange < 0 ? 'cic-green' : actualWeightChange > 0 ? 'cic-red' : ''}`}>
                      {actualWeightChange > 0 ? '+' : ''}{actualWeightChange} lbs
                    </span>
                  </div>
                )}
                <div className="cic-no-target-hint">Set target loss rate for full analysis</div>
              </div>
            ) : (
              <div className="cic-no-data">No calorie data for period</div>
            )}
          </>
        )}
      </div>

      {/* Activity Level Dropdown */}
      <div className="cic-footer">
        <div className="cic-activity-row">
          <select
            className="cic-activity-select"
            value={activityLevel}
            onChange={(e) => handleActivityChange(e.target.value)}
            disabled={saving}
          >
            {Object.entries(ACTIVITY_MULTIPLIERS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .cic-calculator {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .cic-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 0;
  }

  .cic-section {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .cic-section + .cic-section {
    border-top: 1px solid var(--border-secondary);
    padding-top: 8px;
    margin-top: 4px;
  }

  .cic-period-label {
    font-size: 10px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }

  .cic-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 2px 0;
  }

  .cic-row-highlight {
    background: var(--bg-tertiary);
    border-radius: 4px;
    padding: 4px 6px;
    margin: 2px -6px;
  }

  .cic-row-gap {
    border-radius: 4px;
    padding: 3px 6px;
    margin: 2px -6px;
  }

  .cic-row-gap-red {
    background: rgba(239, 68, 68, 0.08);
    border: 1px solid rgba(239, 68, 68, 0.15);
  }

  .cic-row-gap-green {
    background: rgba(34, 197, 94, 0.08);
    border: 1px solid rgba(34, 197, 94, 0.15);
  }

  .cic-row-gap-neutral {
    background: var(--bg-tertiary);
    border: 1px solid var(--border-secondary);
  }

  .cic-row-clickable {
    cursor: pointer;
    user-select: none;
  }

  .cic-row-detail {
    padding: 0 0 2px;
  }

  .cic-detail-text {
    font-size: 10px;
    color: var(--text-tertiary);
    font-style: italic;
  }

  .cic-divider {
    border-top: 1px dashed var(--border-secondary);
    margin: 4px 0;
  }

  .cic-actual-value {
    font-size: 15px;
    font-weight: 700;
  }

  .cic-no-target-hint {
    font-size: 10px;
    color: var(--text-tertiary);
    font-style: italic;
    text-align: center;
    margin-top: 4px;
  }

  .cic-label {
    font-size: 12px;
    color: var(--text-secondary);
  }

  .cic-value {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
  }

  .cic-tdee-value {
    font-size: 16px;
    color: var(--accent-blue);
  }

  .cic-green { color: #22c55e; }
  .cic-red { color: #ef4444; }

  .cic-missing {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    gap: 4px;
    color: var(--text-tertiary);
  }

  .cic-missing-text {
    font-size: 13px;
    font-weight: 500;
  }

  .cic-missing-detail {
    font-size: 11px;
  }

  .cic-no-data {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    color: var(--text-tertiary);
    font-size: 12px;
  }

  .cic-footer {
    border-top: 1px solid var(--border-secondary);
    padding-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .cic-activity-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .cic-activity-select {
    flex: 1;
    padding: 4px 6px;
    font-size: 12px;
    background: var(--bg-tertiary);
    color: var(--text-primary);
    border: 1px solid var(--border-secondary);
    border-radius: 4px;
    cursor: pointer;
  }

  .cic-activity-select:focus {
    outline: none;
    border-color: var(--accent-blue);
  }

  .cic-tdee-inline {
    font-size: 12px;
    font-weight: 600;
    color: var(--accent-blue);
    white-space: nowrap;
  }

  .cic-target-row {
    padding: 0 2px;
  }

  .cic-target-label {
    font-size: 10px;
    color: var(--text-tertiary);
  }

  .cic-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 120px;
  }

  .cic-loading .loading-spinner {
    width: 20px;
    height: 20px;
    border: 2px solid var(--border-secondary);
    border-top-color: var(--accent-blue);
    border-radius: 50%;
    animation: cic-spin 1s linear infinite;
  }

  @keyframes cic-spin {
    to { transform: rotate(360deg); }
  }
`;

export default CICCalculatorTile;
